import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, ExternalLink, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isQuotaError,
  getQuotaMessage,
  onQuotaError,
  goToQuotasPage,
  type QuotaErrorDetails,
} from "@/lib/quotaError";

interface Props {
  children: ReactNode;
  /** Re-thrown for non-quota errors so a parent boundary can handle them. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  /** Active quota notice (from a thrown error or an emitted event). */
  notice: QuotaErrorDetails | null;
  /** Non-quota error, surfaced through the fallback or default message. */
  otherError: Error | null;
}

/**
 * Dedicated boundary for QUOTA_EXHAUSTED failures.
 *
 * - Synchronous render-time errors are caught via React's lifecycle.
 * - Async errors (fetch / edge-function calls) emit a global event from
 *   `lib/quotaError.ts::emitQuotaError` and are picked up here too.
 *
 * In both cases the user sees the server message and an "Acheter des
 * crédits" CTA — never a blank screen.
 */
export class QuotaErrorBoundary extends Component<Props, State> {
  state: State = { notice: null, otherError: null };
  private unsubscribe: (() => void) | null = null;

  static getDerivedStateFromError(error: Error): Partial<State> {
    if (isQuotaError(error)) {
      return {
        notice: {
          message: getQuotaMessage(error),
          quotaType: (error as any)?.quotaType,
          onRetry: (error as any)?.onRetry,
        },
      };
    }
    return { otherError: error };
  }

  componentDidMount() {
    this.unsubscribe = onQuotaError((details) => {
      this.setState({ notice: details });
    });
  }

  componentWillUnmount() {
    this.unsubscribe?.();
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (!isQuotaError(error)) {
      // eslint-disable-next-line no-console
      console.error("[QuotaErrorBoundary] non-quota error:", error, info.componentStack);
    } else {
      // eslint-disable-next-line no-console
      console.warn("[QuotaErrorBoundary] quota error caught:", error.message);
    }
  }

  private dismiss = () => this.setState({ notice: null });
  private resetOther = () => this.setState({ otherError: null });

  private handleRetry = () => {
    const retry = this.state.notice?.onRetry;
    this.dismiss();
    retry?.();
  };

  render() {
    const { notice, otherError } = this.state;

    if (otherError) {
      if (this.props.fallback) return this.props.fallback(otherError, this.resetOther);
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <p>Une erreur est survenue.</p>
          <Button size="sm" variant="outline" onClick={this.resetOther} className="gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Réessayer
          </Button>
        </div>
      );
    }

    return (
      <>
        {this.props.children}
        {notice && (
          <div
            role="alertdialog"
            aria-modal="false"
            aria-live="assertive"
            className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-lg border border-amber-500/40 bg-background shadow-xl p-4 animate-in slide-in-from-bottom-4"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-amber-500/10 p-1.5 mt-0.5">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold mb-1">Quota épuisé</p>
                <p className="text-xs text-muted-foreground break-words">
                  {notice.message}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <Button
                    size="sm"
                    className="h-9 px-2 text-xs gap-1"
                    onClick={() => {
                      this.dismiss();
                      goToQuotasPage();
                    }}
                  >
                    <ExternalLink className="w-3 h-3" /> Acheter des crédits
                  </Button>
                  {notice.onRetry && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 px-2 text-xs gap-1"
                      onClick={this.handleRetry}
                    >
                      <RotateCcw className="w-3 h-3" /> Re-tenter
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-9 px-2 text-xs"
                    onClick={this.dismiss}
                  >
                    Fermer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default QuotaErrorBoundary;
